import { useMemo, useState } from 'react';
import { Pencil, Trash2, HardHat, KeyRound, Scissors, ClipboardList } from 'lucide-react';
import DataTable from '../ui/DataTable';
import ChipPicker from '../ui/ChipPicker';
import { StatusBadge } from '../ui/Badge';
import AssignWorkModal from './AssignWorkModal';

// The full Worker / Employee master field set. Today's /api/workers only
// persists a subset (name, phone, role, status), so the rest render as '—'
// until the backend stores them. Rarely-used columns default to hidden and
// can be switched on from the Columns menu.
const dash = <span className="text-content-subtle">—</span>;
const text = (v) => (v ? String(v) : '');

export default function WorkersTable({
  workers, loading, error, onRowClick, onEdit, onDelete, onBulkDelete, onLogin,
  // What this login may do here. An office login gets everything; a portal
  // login gets what the admin switched on for their group.
  mayEdit = true, mayAssign = true,
}) {
  // "Who can make this?" — filtered here rather than on the server, because
  // the whole list is already loaded and the answer should be instant.
  const [skillFilter, setSkillFilter] = useState(null);
  // The worker the "Assign" dialog is open for.
  const [assignTo, setAssignTo] = useState(null);

  const skillOptions = useMemo(() => {
    const seen = new Map();
    for (const w of workers || []) {
      for (const s of w.skills || []) if (!seen.has(s.id)) seen.set(s.id, s.name);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [workers]);

  const shownWorkers = useMemo(() => (skillFilter
    ? (workers || []).filter((w) => (w.skills || []).some((s) => s.id === skillFilter))
    : workers), [workers, skillFilter]);

  const skillName = skillOptions.find((o) => o.id === skillFilter)?.name;
  if (error) {
    return <div className="card px-4 py-8 text-center text-sm text-danger-500">{error}</div>;
  }

  const columns = [
    {
      key: 'code',
      header: 'Code',
      width: 'w-20',
      exportValue: (w) => text(w.code || w.id),
      render: (w) => <span className="font-mono text-xs text-content-muted">{w.code || w.id || '—'}</span>,
    },
    {
      key: 'ledgerName',
      header: 'Worker Name',
      hideable: false,
      sortValue: (w) => w.ledgerName || w.name || '',
      exportValue: (w) => text(w.ledgerName || w.name),
      render: (w) => <span className="font-medium text-content">{w.ledgerName || w.name || '—'}</span>,
    },
    {
      key: 'skills',
      header: 'Skills',
      width: 'w-56',
      sortValue: (w) => (w.skills || []).length,
      exportValue: (w) => (w.skills || []).map((s) => s.name).join(', '),
      render: (w) => {
        const list = w.skills || [];
        if (!list.length) return <span className="text-content-subtle" title="No garments set">—</span>;
        // Two names and a count: the whole list would push every other column
        // off the screen, and the full set is one hover away.
        const shown = list.slice(0, 2);
        return (
          <span className="flex flex-wrap items-center gap-1" title={list.map((s) => s.name).join(', ')}>
            {shown.map((s) => <span key={s.id} className="badge-brand">{s.name}</span>)}
            {list.length > shown.length && (
              <span className="text-2xs text-content-subtle">+{list.length - shown.length}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'acGroup',
      header: 'Group',
      sortValue: (w) => w.acGroup || w.role || '',
      exportValue: (w) => text(w.acGroup || w.role),
      render: (w) => (w.acGroup || w.role
        ? <span className="badge-neutral">{(w.acGroup || w.role).replace(/_/g, ' ')}</span>
        : dash),
    },
    {
      key: 'mobNo',
      header: 'Mobile',
      sortValue: (w) => w.mobNo || w.phone || '',
      exportValue: (w) => text(w.mobNo || w.phone),
      render: (w) => {
        const m = w.mobNo || w.phone;
        return m ? <a href={`tel:${m}`} className="font-medium text-brand-600 hover:underline">{m}</a> : dash;
      },
    },
    { key: 'city', header: 'City', exportValue: (w) => text(w.city), render: (w) => w.city || dash },
    { key: 'status', header: 'Status', render: (w) => <StatusBadge status={w.status} /> },
    {
      key: 'portal',
      header: 'Portal',
      sortValue: (w) => (w.hasLogin ? 1 : 0),
      exportValue: (w) => w.loginName || '',
      render: (w) => (w.hasLogin
        ? <span className="font-mono text-xs text-brand-700">{w.loginName}</span>
        : dash),
    },

    // --- secondary fields, hidden by default -------------------------------
    { key: 'add1', header: 'Address 1', defaultHidden: true, exportValue: (w) => text(w.add1), render: (w) => w.add1 || dash },
    { key: 'add2', header: 'Address 2', defaultHidden: true, exportValue: (w) => text(w.add2), render: (w) => w.add2 || dash },
    { key: 'country', header: 'Country', defaultHidden: true, exportValue: (w) => text(w.country), render: (w) => w.country || dash },
    { key: 'mobNo2', header: 'Mobile 2', defaultHidden: true, exportValue: (w) => text(w.mobNo2), render: (w) => w.mobNo2 || dash },
    {
      key: 'vatNo',
      header: 'GST No.',
      defaultHidden: true,
      exportValue: (w) => text(w.vatNo || w.gstNo),
      render: (w) => (w.vatNo || w.gstNo
        ? <span className="font-mono text-xs">{w.vatNo || w.gstNo}</span>
        : dash),
    },
    {
      key: 'panNo',
      header: 'PAN No.',
      defaultHidden: true,
      exportValue: (w) => text(w.panNo),
      render: (w) => (w.panNo ? <span className="font-mono text-xs">{w.panNo}</span> : dash),
    },
    { key: 'taxation', header: 'Taxation', defaultHidden: true, exportValue: (w) => text(w.taxation), render: (w) => w.taxation || dash },

    {
      key: 'actions',
      header: '',
      sortable: false,
      hideable: false,
      align: 'right',
      exportValue: () => '',
      render: (w) => (
        <div className="flex items-center justify-end gap-0.5">
          {/* Handing work out from the worker's side: somebody is free now,
              so find them an order. A supervisor runs the floor rather than
              sewing on it, so there is nothing to hand them — the server
              refuses it, and offering a button that can only fail is worse
              than not offering one. */}
          {mayAssign
            && !w.isSupervisor
            && w.status === 'ACTIVE'
            && w.role !== 'NO_TRADE'
            && !['STORE_MANAGER', 'WORKSHOP_MANAGER'].includes(w.loginRole) && (
            <button
              className="btn-outline btn-sm mr-1"
              title={`Give work to ${w.ledgerName || w.name}`}
              onClick={(e) => { e.stopPropagation(); setAssignTo(w); }}
            >
              <ClipboardList size={13} /> Assign
            </button>
          )}
          {onLogin && (
            <button
              className={`btn-icon ${w.hasLogin ? 'text-brand-600' : ''}`}
              title={w.hasLogin ? 'Portal login (reset / remove)' : 'Create portal login'}
              onClick={() => onLogin(w)}
            >
              <KeyRound size={15} />
            </button>
          )}
          {mayEdit && onEdit && (
            <button className="btn-icon" title="Edit" onClick={() => onEdit(w)}><Pencil size={15} /></button>
          )}
          {onDelete && (
            <button
              className="btn-icon hover:bg-danger-50 hover:text-danger-600"
              title="Delete"
              onClick={() => onDelete(w)}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
    <DataTable
      columns={columns}
      rows={shownWorkers}
      loading={loading}
      storageKey="workers"
      toolbarExtra={skillOptions.length > 0 && (
        <ChipPicker
          header="Filter by skill"
          title="Show only the workers with this skill"
          ariaLabel="Filter by skill"
          options={[
            { value: '', label: 'Everyone', hint: 'no filter', selected: !skillFilter },
            ...skillOptions.map((o) => ({
              value: String(o.id),
              label: o.name,
              hint: `${(workers || []).filter((w) => (w.skills || []).some((s) => s.id === o.id)).length} worker(s)`,
              selected: skillFilter === o.id,
            })),
          ]}
          onPick={(v) => setSkillFilter(v ? Number(v) : null)}
        >
          <span className={`input input-sm flex items-center gap-1.5 ${skillFilter ? 'font-semibold text-brand-700' : ''}`}>
            <Scissors size={13} className="shrink-0 text-content-subtle" />
            {skillName || 'Skill: any'}
          </span>
        </ChipPicker>
      )}
      searchPlaceholder="Search name, code, mobile, city…"
      searchKeys={['code', 'ledgerName', 'mobNo', 'city', 'acGroup']}
      selectable={Boolean(onBulkDelete)}
      exportable
      exportFilename="workers"
      onRowClick={onRowClick}
      emptyTitle="No workers yet"
      emptyHint='Click "Add Worker" to add your first worker.'
      bulkActions={onBulkDelete ? (rows) => (
        <button className="btn-danger btn-sm" onClick={() => onBulkDelete(rows)}>
          <Trash2 size={13} /> Delete {rows.length}
        </button>
      ) : undefined}
    />

    <AssignWorkModal
      worker={assignTo}
      open={!!assignTo}
      onClose={() => setAssignTo(null)}
    />
    </>
  );
}
